import { useEffect, useRef, useState } from "react";
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX } from "lucide-react";

function fmt(t) {
  if (!isFinite(t)) return "0:00";
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function AudioPlayer({ src, onTime, audioRef }) {
  const localRef = useRef(null);
  const ref = audioRef || localRef;
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);

  useEffect(() => {
    const a = ref.current;
    if (!a) return;
    const onLoad = () => setDuration(a.duration || 0);
    const onTimeUpdate = () => {
      setTime(a.currentTime);
      if (onTime) onTime(a.currentTime, a.duration);
    };
    const onEnd = () => setPlaying(false);
    a.addEventListener("loadedmetadata", onLoad);
    a.addEventListener("timeupdate", onTimeUpdate);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("loadedmetadata", onLoad);
      a.removeEventListener("timeupdate", onTimeUpdate);
      a.removeEventListener("ended", onEnd);
    };
  }, [onTime]);

  const toggle = () => {
    const a = ref.current;
    if (!a) return;
    if (a.paused) { a.play(); setPlaying(true); }
    else { a.pause(); setPlaying(false); }
  };

  const seekBy = (delta) => {
    const a = ref.current;
    if (!a) return;
    a.currentTime = Math.max(0, Math.min((a.duration || 0), a.currentTime + delta));
  };

  const onScrub = (e) => {
    const a = ref.current;
    if (!a) return;
    const v = Number(e.target.value);
    a.currentTime = v;
    setTime(v);
  };

  const toggleMute = () => {
    const a = ref.current;
    if (!a) return;
    a.muted = !a.muted;
    setMuted(a.muted);
  };

  return (
    <div data-testid="audio-player" className="border border-zinc-950 bg-white p-6">
      <audio ref={ref} src={src} preload="metadata" />

      <div className="flex items-center gap-4">
        <button
          data-testid="player-skip-back"
          onClick={() => seekBy(-15)}
          className="p-2 text-zinc-600 hover:text-zinc-950 transition-colors"
          aria-label="Skip back 15 seconds"
        >
          <SkipBack className="h-5 w-5" />
        </button>

        <button
          data-testid="player-play-pause"
          onClick={toggle}
          className="flex h-20 w-20 items-center justify-center bg-zinc-950 text-white transition-colors hover:bg-rose-600"
          aria-label={playing ? "Pause" : "Play"}
        >
          {playing ? <Pause className="h-9 w-9" /> : <Play className="ml-1 h-9 w-9" />}
        </button>

        <button
          data-testid="player-skip-forward"
          onClick={() => seekBy(15)}
          className="p-2 text-zinc-600 hover:text-zinc-950 transition-colors"
          aria-label="Skip forward 15 seconds"
        >
          <SkipForward className="h-5 w-5" />
        </button>

        <div className="ml-2 flex flex-1 flex-col gap-2">
          <input
            data-testid="player-scrubber"
            className="news-scrub"
            type="range"
            min={0}
            max={duration || 0}
            step={0.1}
            value={time}
            onChange={onScrub}
          />
          <div className="flex justify-between font-mono text-xs uppercase tracking-widest text-zinc-500">
            <span data-testid="player-time-current">{fmt(time)}</span>
            <span data-testid="player-time-total">{fmt(duration)}</span>
          </div>
        </div>

        <button
          data-testid="player-mute"
          onClick={toggleMute}
          className="p-2 text-zinc-600 hover:text-zinc-950 transition-colors"
          aria-label={muted ? "Unmute" : "Mute"}
        >
          {muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
        </button>
      </div>
    </div>
  );
}
